import React from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { 
    faTimes, 
    faArrowRight, 
    faInfoCircle,
    faUser,
    faCalendar,
    faFileAlt,
    faCheckCircle,
    faTimesCircle
} from '@fortawesome/free-solid-svg-icons';
import { useHistory } from 'react-router-dom';
import './RFCDetailsModal.css';

/**
 * CRM RFC Notification Modal
 * 
 * Information-only modal for displaying RFC details in notifications.
 * Does NOT include approval/rejection actions - just shows what changed.
 * Users must navigate to the CRM profile to review and approve/reject.
 * 
 * Props:
 * - isOpen: boolean - Modal visibility
 * - onClose: function - Close callback
 * - notification: object - Notification object containing RFC metadata
 */
const CRMRFCNotificationModal = ({ isOpen, onClose, notification }) => {
    const history = useHistory();
    
    if (!isOpen || !notification) return null;
    
    const metadata = notification.metadata || {};
    const notificationType = notification.notification_type;
    const isReviewedNotification = notificationType === 'crm_rfc_reviewed';
    
    // For reviewed notifications, field_decisions is in metadata
    // For request notifications, changes_data is in metadata
    const changesData = metadata.changes_data || {};
    const fieldDecisions = metadata.field_decisions || {};
    const changeCount = Object.keys(changesData).length;
    
    const handleOverlayClick = (e) => {
        if (e.target === e.currentTarget) {
            onClose();
        }
    };
    
    const handleViewRFC = () => {
        const profileId = metadata.profile_id;
        if (profileId) {
            onClose();
            history.push(`/dashboard/crm/profile/${profileId}`);
        }
    };
    
    const formatDate = (dateString) => {
        if (!dateString) return '';
        const date = new Date(dateString);
        return date.toLocaleString('en-US', {
            year: 'numeric',
            month: 'short',
            day: 'numeric',
            hour: '2-digit',
            minute: '2-digit'
        });
    };
    
    const formatFieldName = (fieldName) => {
        return fieldName
            .replace(/_/g, ' ')
            .replace(/\b\w/g, char => char.toUpperCase());
    };
    
    const formatNestedValue = (val) => {
        // Helper function to safely format nested values
        if (val === null || val === undefined || val === '') {
            return '(empty)';
        }
        if (typeof val === 'boolean') {
            return val ? 'Yes' : 'No';
        }
        if (typeof val === 'object') {
            // If it's an object, stringify it nicely
            if (val.formatted_address) {
                return val.formatted_address;
            }
            return JSON.stringify(val, null, 2);
        }
        return String(val);
    };
    
    // Helper to find differences between two arrays of objects
    const findArrayDifferences = (oldArray, newArray) => {
        if (!Array.isArray(oldArray) || !Array.isArray(newArray)) {
            return null;
        }
        
        const differences = [];
        
        // Try to match by ID or index
        newArray.forEach((newItem, idx) => {
            if (typeof newItem !== 'object' || newItem === null) return;
            
            // Find matching old item by id or index
            const itemId = newItem.id || newItem.Id;
            let oldItem = null;
            
            if (itemId) {
                oldItem = oldArray.find(old => (old.id || old.Id) === itemId);
            } else {
                oldItem = oldArray[idx];
            }
            
            if (!oldItem) {
                // New item added
                differences.push({
                    type: 'added',
                    index: idx + 1,
                    item: newItem,
                    changes: null
                });
            } else {
                // Check for changes
                const changes = {};
                Object.keys(newItem).forEach(key => {
                    if (JSON.stringify(newItem[key]) !== JSON.stringify(oldItem[key])) {
                        changes[key] = {
                            old: oldItem[key],
                            new: newItem[key]
                        };
                    }
                });
                
                if (Object.keys(changes).length > 0) {
                    differences.push({
                        type: 'modified',
                        index: idx + 1,
                        item: newItem,
                        changes: changes
                    });
                }
            }
        });
        
        // Check for removed items
        if (oldArray.length > newArray.length) {
            for (let i = newArray.length; i < oldArray.length; i++) {
                differences.push({
                    type: 'removed',
                    index: i + 1,
                    item: oldArray[i],
                    changes: null
                });
            }
        }
        
        return differences.length > 0 ? differences : null;
    };
    
    const formatValue = (value, oldValue = null) => {
        if (value === null || value === undefined || value === '') {
            return <span className="rfc-empty-value">(empty)</span>;
        }
        
        if (typeof value === 'boolean') {
            return value ? 'Yes' : 'No';
        }
        
        if (Array.isArray(value)) {
            if (value.length === 0) {
                return <span className="rfc-empty-value">(empty)</span>;
            }
            
            // Check if array contains objects (like banking details)
            if (value.length > 0 && typeof value[0] === 'object' && value[0] !== null) {
                // Try to find differences if we have old value
                const differences = oldValue ? findArrayDifferences(oldValue, value) : null;
                
                if (differences) {
                    // Show only what changed
                    return (
                        <div className="rfc-object-list">
                            {differences.map((diff, idx) => (
                                <div key={idx} className={`rfc-object-item ${diff.type === 'added' ? 'rfc-item-added' : diff.type === 'removed' ? 'rfc-item-removed' : ''}`}>
                                    <strong>
                                        Item {diff.index}
                                        {diff.type === 'added' && ' (NEW)'}
                                        {diff.type === 'removed' && ' (REMOVED)'}
                                        {diff.type === 'modified' && ' (Modified)'}:
                                    </strong>
                                    {diff.type === 'modified' && diff.changes && (
                                        <ul className="rfc-object-fields">
                                            {Object.entries(diff.changes).map(([key, change]) => (
                                                <li key={key}>
                                                    <span className="rfc-object-key">{formatFieldName(key)}:</span>{' '}
                                                    <span className="rfc-value-old-inline" style={{textDecoration: 'line-through', color: '#ef4444'}}>
                                                        {formatNestedValue(change.old)}
                                                    </span>
                                                    {' → '}
                                                    <span className="rfc-value-new-inline" style={{fontWeight: 600, color: '#10b981'}}>
                                                        {formatNestedValue(change.new)}
                                                    </span>
                                                </li>
                                            ))}
                                        </ul>
                                    )}
                                    {(diff.type === 'added' || diff.type === 'removed') && (
                                        <ul className="rfc-object-fields">
                                            {Object.entries(diff.item).map(([key, val]) => (
                                                <li key={key}>
                                                    <span className="rfc-object-key">{formatFieldName(key)}:</span>{' '}
                                                    <span className="rfc-object-value">{formatNestedValue(val)}</span>
                                                </li>
                                            ))}
                                        </ul>
                                    )}
                                </div>
                            ))}
                        </div>
                    );
                }
                
                // Fallback: show all items
                return (
                    <div className="rfc-object-list">
                        {value.map((item, idx) => (
                            <div key={idx} className="rfc-object-item">
                                <strong>Item {idx + 1}:</strong>
                                <ul className="rfc-object-fields">
                                    {Object.entries(item).map(([key, val]) => (
                                        <li key={key}>
                                            <span className="rfc-object-key">{formatFieldName(key)}:</span>{' '}
                                            <span className="rfc-object-value">{formatNestedValue(val)}</span>
                                        </li>
                                    ))}
                                </ul>
                            </div>
                        ))}
                    </div>
                );
            }
            
            return value.join(', ');
        }
        
        if (typeof value === 'object') {
            return (
                <ul className="rfc-object-fields">
                    {Object.entries(value).map(([key, val]) => (
                        <li key={key}>
                            <span className="rfc-object-key">{formatFieldName(key)}:</span>{' '}
                            <span className="rfc-object-value">{formatNestedValue(val)}</span>
                        </li>
                    ))}
                </ul>
            );
        }
        
        return String(value);
    };
    
    return (
        <div className="rfc-details-overlay" onClick={handleOverlayClick}>
            <div className="rfc-details-modal">
                {/* Header */}
                <div className="rfc-details-header">
                    <div className="rfc-details-header-content">
                        <h3>{isReviewedNotification ? 'RFC Review Completed' : 'CRM Request For Changes'}</h3>
                        <p className="rfc-details-profile-name">{metadata.profile_name || 'CRM Profile'}</p>
                    </div>
                    <button 
                        className="rfc-details-close-btn" 
                        onClick={onClose}
                        type="button"
                        aria-label="Close"
                    >
                        <FontAwesomeIcon icon={faTimes} />
                    </button>
                </div>
                
                {/* Body */}
                <div className="rfc-details-body">
                    {/* Request/Review Info */}
                    <div className="rfc-details-info">
                        {isReviewedNotification ? (
                            <>
                                <div className="rfc-details-info-row">
                                    <div className="rfc-info-item">
                                        <label>Reviewed By:</label>
                                        <span>{metadata.reviewed_by_name || 'Unknown'}</span>
                                    </div>
                                    
                                    <div className="rfc-info-item">
                                        <label>Reviewed:</label>
                                        <span>{formatDate(metadata.reviewed_at)}</span>
                                    </div>
                                </div>
                                
                                <div className="rfc-details-info-row">
                                    <div className="rfc-info-item">
                                        <label>Status:</label>
                                        <span className={`rfc-change-count ${metadata.is_fully_approved ? 'rfc-approved' : metadata.is_fully_rejected ? 'rfc-rejected' : 'rfc-partial'}`}>
                                            {metadata.is_fully_approved && <><FontAwesomeIcon icon={faCheckCircle} /> Fully Approved</>}
                                            {metadata.is_fully_rejected && <><FontAwesomeIcon icon={faTimesCircle} /> Fully Rejected</>}
                                            {metadata.is_partial && <>Partially Approved</>}
                                        </span>
                                    </div>
                                    
                                    <div className="rfc-info-item">
                                        <label>Result:</label>
                                        <span className="rfc-change-count">{metadata.approved_count} approved, {metadata.rejected_count} rejected</span>
                                    </div>
                                </div>
                            </>
                        ) : (
                            <>
                                <div className="rfc-details-info-row">
                                    <div className="rfc-info-item">
                                        <label>Requested By:</label>
                                        <span>{metadata.requested_by_name || 'Unknown'}
                                            {metadata.requested_by_employee_id && ` (ID: ${metadata.requested_by_employee_id})`}
                                        </span>
                                    </div>
                                    
                                    <div className="rfc-info-item">
                                        <label>Submitted:</label>
                                        <span>{formatDate(notification.created_at)}</span>
                                    </div>
                                </div>
                                
                                <div className="rfc-details-info-row">
                                    <div className="rfc-info-item">
                                        <label>Changes:</label>
                                        <span className="rfc-change-count">{changeCount} field{changeCount !== 1 ? 's' : ''}</span>
                                    </div>
                                </div>
                            </>
                        )}
                    </div>
                    
                    {/* Request/Review Notes */}
                    {metadata.request_notes && !isReviewedNotification && (
                        <div className="rfc-details-info" style={{ marginBottom: '24px' }}>
                            <div className="rfc-notes-section">
                                <label>Request Notes:</label>
                                <p className="rfc-notes-text">{metadata.request_notes}</p>
                            </div>
                        </div>
                    )}
                    
                    {metadata.review_notes && isReviewedNotification && (
                        <div className="rfc-details-info" style={{ marginBottom: '24px' }}>
                            <div className="rfc-notes-section">
                                <label>Review Notes:</label>
                                <p className="rfc-notes-text">{metadata.review_notes}</p>
                            </div>
                        </div>
                    )}
                    
                    {/* Field Changes */}
                    <div className="rfc-changes-section">
                        <h4>{isReviewedNotification ? 'Review Decisions:' : 'Proposed Changes:'}</h4>
                        <div className="rfc-changes-table">
                            {Object.entries(changesData).map(([fieldName, change]) => {
                                const decision = fieldDecisions[fieldName];
                                const isApproved = decision?.decision === 'approved';
                                const isRejected = decision?.decision === 'rejected';
                                
                                return (
                                    <div key={fieldName} className="rfc-change-row">
                                        <div className="rfc-field-name">
                                            {formatFieldName(fieldName)}
                                            {isReviewedNotification && decision && (
                                                <span className={`rfc-decision-badge ${isApproved ? 'approved' : 'rejected'}`}>
                                                    <FontAwesomeIcon icon={isApproved ? faCheckCircle : faTimesCircle} />
                                                    {isApproved ? 'Approved' : 'Rejected'}
                                                </span>
                                            )}
                                        </div>
                                        <div className="rfc-value-comparison">
                                            <div className="rfc-value-box rfc-old-value">
                                                <label>Current:</label>
                                                <div className="rfc-value-content">
                                                    {formatValue(change.old, null)}
                                                </div>
                                            </div>
                                            <div className="rfc-arrow">
                                                <FontAwesomeIcon icon={faArrowRight} />
                                            </div>
                                            <div className="rfc-value-box rfc-new-value">
                                                <label>Proposed:</label>
                                                <div className="rfc-value-content">
                                                    {formatValue(change.new, change.old)}
                                                </div>
                                            </div>
                                        </div>
                                        {isReviewedNotification && decision?.rejection_reason && (
                                            <div className="rfc-rejection-reason">
                                                <label>Rejection Reason:</label>
                                                <p>{decision.rejection_reason}</p>
                                            </div>
                                        )}
                                    </div>
                                );
                            })}
                        </div>
                    </div>
                    
                    {/* Info Box */}
                    {!isReviewedNotification && (
                        <div className="rfc-review-section">
                            <h4>Action Required</h4>
                            <p style={{ margin: 0, color: '#166534' }}>
                                Navigate to the CRM profile to review and approve/reject this RFC.
                            </p>
                        </div>
                    )}
                </div>
                
                {/* Footer */}
                <div className="rfc-details-footer">
                    <button 
                        className="rfc-details-close-button" 
                        onClick={onClose}
                        type="button"
                        style={{ marginRight: '12px', background: '#6b7280' }}
                    >
                        Close
                    </button>
                    <button 
                        className="rfc-details-close-button" 
                        onClick={handleViewRFC}
                        type="button"
                    >
                        <FontAwesomeIcon icon={faArrowRight} />
                        {isReviewedNotification ? 'View Profile' : 'View RFC in Profile'}
                    </button>
                </div>
            </div>
        </div>
    );
};

export default CRMRFCNotificationModal;

